<div class="space-y-8">
  <!-- Step Header -->
  <div class="max-w-3xl">
    <h2 class="text-3xl font-bold tracking-tight text-neutral-12">Parcelle cadastrale</h2>
    <p class="mt-2 text-lg text-neutral-11">
      Identifiez et validez la ou les parcelles cadastrales officielles de votre bien. Ces données permettent
      d'extraire automatiquement l'année de construction, les matériaux et les niveaux du bâtiment.
    </p>
  </div>

  <!-- Locked banner if already created -->
  @if (isLocked()) {
    <div
      class="flex items-center gap-3.5 rounded-2xl border border-gray-6 bg-gray-2 p-4 text-gray-12 shadow-2xs"
    >
      <mat-icon class="text-gray-11 text-xl shrink-0">lock</mat-icon>
      <div class="text-xs sm:text-sm">
        <span class="font-bold">Parcelle cadastrale verrouillée.</span>
        <span class="text-gray-11">
          La localisation et le cadastre sont définitifs et ne peuvent plus être modifiés après création de
          l'annonce.</span
        >
      </div>
    </div>
  }

  <!-- Manual Edition Card (when opened) -->
  @if (!isLocked() && isEditingManual()) {
    <div class="rounded-2xl border border-jade-a6 bg-jade-a1 p-6 shadow-sm">
      <div class="flex items-center justify-between gap-2 mb-4">
        <div class="flex items-center gap-2">
          <mat-icon class="text-jade-11 text-lg leading-none">edit</mat-icon>
          <span class="text-base font-bold text-gray-12">Recherche ou ajout manuel</span>
        </div>
        <button
          type="button"
          class="text-xs font-medium text-gray-11 hover:text-gray-12 transition-colors cursor-pointer"
          (click)="cancelManualEdit()"
        >
          Fermer
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <!-- Input 1: Numéro ou référence -->
        <div>
          <label for="step-cadastral-ref-input" class="block text-xs font-semibold text-gray-11 mb-1.5">
            Numéro ou référence de parcelle
          </label>
          <div class="relative">
            <input
              id="step-cadastral-ref-input"
              type="text"
              class="w-full rounded-xl border border-gray-6 bg-surface-default pl-3.5 pr-9 py-2.5 text-sm text-gray-12 font-mono placeholder:text-gray-9 focus:border-jade-8 focus:outline-none focus:ring-1 focus:ring-jade-8 shadow-2xs"
              placeholder="Ex. 0223, BT 0223 ou 64024000BT0223"
              [ngModel]="manualReference()"
              (ngModelChange)="onManualReferenceInput($event)"
            />
            @if (isResolvingParcel()) {
              <div class="absolute right-3 top-1/2 -translate-y-1/2">
                <mat-spinner diameter="16"></mat-spinner>
              </div>
            }
          </div>
          <p class="mt-1.5 text-xs text-gray-10">
            Ex. <span class="font-mono text-gray-11">0223</span>,
            <span class="font-mono text-gray-11">BT 0223</span> ou référence complète 14 caractères.
          </p>
        </div>

        <!-- Input 2: Superficie officielle (lecture seule) -->
        <div>
          <span class="block text-xs font-semibold text-gray-11 mb-1.5">
            Superficie officielle du cadastre
          </span>

          <div
            class="flex items-center justify-between w-full h-[42px] rounded-xl border px-3.5 select-none transition-colors shadow-2xs"
            [class.border-jade-a6]="resolutionStatus() === 'found'"
            [class.bg-jade-a2/40]="resolutionStatus() === 'found'"
            [class.border-ruby-a6]="resolutionStatus() === 'not_found'"
            [class.bg-ruby-a2/30]="resolutionStatus() === 'not_found'"
            [class.border-gray-6]="resolutionStatus() !== 'found' && resolutionStatus() !== 'not_found'"
            [class.bg-gray-2/70]="resolutionStatus() !== 'found' && resolutionStatus() !== 'not_found'"
          >
            @if (isResolvingParcel()) {
              <div class="flex items-center gap-2 text-xs text-gray-11">
                <mat-spinner diameter="14"></mat-spinner>
                <span>Recherche dans la base cadastrale...</span>
              </div>
            } @else if (manualSurface()) {
              <span class="font-bold text-gray-12 text-sm font-mono whitespace-nowrap">
                {{ manualSurface() }} m²
              </span>
              @if (resolvedParcel()?.hasBdnbBuilding === false) {
                <span
                  class="inline-flex items-center gap-1 text-xs font-semibold text-amber-11 shrink-0 bg-amber-a3 border border-amber-a5 px-2 py-0.5 rounded-full"
                  matTooltip="Cette parcelle ne comporte aucun bâtiment enregistré dans la base BDNB"
                >
                  <mat-icon class="text-xs leading-none">info</mat-icon>
                  <span>Non bâti (BDNB)</span>
                </span>
              } @else if (resolvedParcel()?.hasBdnbBuilding === true) {
                <span
                  class="inline-flex items-center gap-1 text-xs font-semibold text-jade-11 shrink-0 bg-jade-a3 border border-jade-a5 px-2 py-0.5 rounded-full"
                  matTooltip="Bâtiment répertorié au Registre national des bâtiments"
                >
                  <mat-icon class="text-xs leading-none">verified</mat-icon>
                  <span>Bâti certifié BDNB</span>
                </span>
              } @else if (manualSurface()) {
                <span
                  class="inline-flex items-center gap-1 text-xs font-semibold text-jade-11 shrink-0 bg-jade-a3 border border-jade-a5 px-2 py-0.5 rounded-full"
                  matTooltip="Superficie certifiée par le cadastre national"
                >
                  <mat-icon class="text-xs leading-none">verified</mat-icon>
                  <span>Certifié</span>
                </span>
              }
            } @else if (resolutionStatus() === 'not_found') {
              <span class="text-xs text-ruby-11 font-medium flex items-center gap-1.5">
                <mat-icon class="text-sm leading-none">error_outline</mat-icon>
                <span>Parcelle introuvable</span>
              </span>
            } @else {
              <span class="text-xs text-gray-10 italic"> Calculée automatiquement </span>
            }
          </div>
          <p class="mt-1.5 text-xs text-gray-10">
            Contenance au sol certifiée par la Direction Générale des Finances Publiques.
          </p>
        </div>
      </div>

      @if (resolvedParcel() && resolvedParcel()?.hasBdnbBuilding === false) {
        <div
          class="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-a6 bg-amber-a2 p-3 text-xs text-amber-12"
        >
          <mat-icon class="text-amber-10 shrink-0 text-base leading-none mt-0.5">info</mat-icon>
          <div>
            <span class="font-semibold">Parcelle non bâtie :</span>
            aucun bâtiment n'est répertorié sur cette référence au Registre national des bâtiments (BDNB).
            Vous pouvez l'ajouter à votre bien (ex. jardin, cour, terrain, garage).
          </div>
        </div>
      }

      <div class="mt-4 flex items-center justify-end gap-2.5">
        <button
          type="button"
          class="rounded-lg border border-gray-6 px-4 py-2 text-xs font-medium text-gray-11 hover:bg-gray-3 transition-colors cursor-pointer"
          (click)="cancelManualEdit()"
        >
          Annuler
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg bg-jade-9 hover:bg-jade-10 text-jade-contrast px-5 py-2 text-xs font-semibold shadow-xs transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="isResolvingParcel() || !manualReference().trim() || resolutionStatus() === 'not_found'"
          (click)="saveManualEdit()"
        >
          <span>Ajouter cette parcelle</span>
        </button>
      </div>
    </div>
  }

  <!-- Stat Overview Cards -->
  <div class="flex flex-col gap-4">
    <!-- 1. Adresse rattachée -->
    <div
      class="rounded-2xl border border-gray-6 bg-surface-default p-6 flex flex-col justify-between shadow-xs sm:col-span-4"
    >
      <div>
        <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-2">
          <span>Adresse rattachée</span>
          <mat-icon fontIcon="place" class="text-base leading-none text-gray-11"></mat-icon>
        </div>

        <div class="mt-2">
          <span class="text-sm font-semibold text-gray-12 line-clamp-2 leading-snug">
            {{ addressLabel() || 'Adresse définie à l’étape précédente' }}
          </span>
        </div>
      </div>
      @if (coordinates()?.latitude && coordinates()?.longitude) {
        <div class="mt-4 text-xs text-gray-11 font-mono">
          GPS : {{ coordinates()?.latitude | number: '1.4-4' }},
          {{ coordinates()?.longitude | number: '1.4-4' }}
        </div>
      } @else {
        <p class="mt-4 text-xs text-gray-11 m-0">Pointage géographique issu de la Base Adresse Nationale.</p>
      }
    </div>

    <!-- 2. Références parcelles -->
    <div
      class="rounded-2xl border border-gray-6 bg-surface-default p-6 flex flex-col justify-between shadow-xs sm:col-span-5"
    >
      <div>
        <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-2">
          <div class="flex items-center gap-2">
            <span>Parcelles cadastrales ({{ parcels().length }})</span>
            @if (primaryParcel(); as primary) {
              <span class="text-xs text-jade-11 font-semibold hidden sm:inline">
                · Principale : <span class="font-mono">{{ primary.section }} {{ primary.numero }}</span>
              </span>
            }
          </div>
          <div class="flex items-center gap-2">
            @if (!isLocked() && !isEditingManual()) {
              <button
                type="button"
                class="inline-flex items-center gap-1 text-xs font-semibold text-jade-11 hover:text-jade-12 transition-colors cursor-pointer"
                (click)="startManualEdit()"
              >
                <mat-icon class="text-xs leading-none">add</mat-icon>
                <span>Ajouter</span>
              </button>
            }
          </div>
        </div>

        @if (parcels().length > 0) {
          <div class="mt-3 space-y-2.5 max-h-60 overflow-y-auto pr-1">
            @for (parcel of parcels(); track parcel.reference) {
              <div
                class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl p-3 transition-all border shadow-2xs"
                [class.bg-jade-a2]="parcel.isPrimary"
                [class.border-jade-a6]="parcel.isPrimary"
                [class.bg-gray-2]="!parcel.isPrimary"
                [class.border-gray-5]="!parcel.isPrimary"
              >
                <!-- Infos parcelle & rôle -->
                <div class="flex items-center gap-3 min-w-0">
                  @if (parcel.isPrimary) {
                    <div
                      class="flex h-8 w-8 items-center justify-center rounded-lg bg-jade-9 text-white shadow-xs shrink-0"
                      matTooltip="Parcelle principale portant le logement"
                    >
                      <mat-icon class="text-base leading-none text-white">star</mat-icon>
                    </div>
                  } @else {
                    <div
                      class="flex h-8 w-8 items-center justify-center rounded-lg bg-neutral-6 text-white border border-neutral-7 shadow-xs shrink-0"
                      matTooltip="Parcelle secondaire"
                    >
                      <mat-icon class="text-base leading-none text-white">layers</mat-icon>
                    </div>
                  }

                  <div class="min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="font-mono text-sm font-bold text-gray-12 select-all">
                        Section {{ parcel.section }} · N° {{ parcel.numero }}
                      </span>

                      @if (parcel.isPrimary) {
                        <span
                          class="inline-flex items-center gap-1 rounded-full bg-jade-a3 border border-jade-a5 px-2 py-0.5 text-2xs font-bold text-jade-11 shrink-0"
                        >
                          Parcelle principale
                        </span>
                      } @else {
                        <span
                          class="inline-flex items-center rounded-full bg-gray-3 border border-gray-6 px-2 py-0.5 text-2xs font-medium text-gray-11 shrink-0"
                        >
                          Secondaire
                        </span>
                      }

                      @if (parcel.hasBdnbBuilding === true) {
                        <span
                          class="inline-flex items-center gap-1 rounded-full bg-blue-a3 border border-blue-a5 px-2 py-0.5 text-2xs font-semibold text-blue-11 shrink-0"
                          matTooltip="Bâtiment répertorié au Registre national des bâtiments (BDNB)"
                        >
                          <mat-icon class="text-xs leading-none">apartment</mat-icon>
                          <span>Bâti</span>
                        </span>
                      } @else if (parcel.hasBdnbBuilding === false) {
                        <span
                          class="inline-flex items-center gap-1 rounded-full bg-amber-a3 border border-amber-a5 px-2 py-0.5 text-2xs font-semibold text-amber-11 shrink-0"
                          matTooltip="Aucun bâtiment répertorié sur cette parcelle (terrain, jardin, cour, dépendance)"
                        >
                          <mat-icon class="text-xs leading-none">grass</mat-icon>
                          <span>Non bâti</span>
                        </span>
                      }
                    </div>

                    <div class="mt-0.5 flex items-center gap-2 text-xs text-gray-11">
                      @if (parcel.surface) {
                        <span class="font-medium text-gray-12"
                          >{{ parcel.surface | number: '1.0-0' : 'fr-FR' }} m²</span
                        >
                        <span>•</span>
                      }
                      <span class="font-mono text-2xs text-gray-10 select-all">{{ parcel.reference }}</span>
                    </div>
                  </div>
                </div>

                <!-- Actions -->
                @if (!isLocked()) {
                  <div class="flex items-center gap-2 shrink-0 self-end sm:self-center">
                    @if (!parcel.isPrimary && parcels().length > 1) {
                      @if (canBePrimary(parcel)) {
                        <button
                          type="button"
                          class="inline-flex items-center gap-1.5 rounded-lg border border-gray-6 bg-surface-default hover:border-jade-8 hover:text-jade-11 px-2.5 py-1 text-xs font-semibold text-gray-11 transition-all cursor-pointer shadow-2xs"
                          (click)="setPrimaryParcel(parcel.reference)"
                          matTooltip="Définir comme la parcelle principale portant le bâtiment"
                        >
                          <mat-icon class="text-sm leading-none">star_border</mat-icon>
                          <span>Définir comme principale</span>
                        </button>
                      } @else {
                        <span
                          class="inline-flex items-center gap-1 text-2xs text-gray-9 italic px-2 py-1"
                          matTooltip="Une parcelle non bâtie ne peut pas être désignée comme principale lorsqu'une parcelle bâtie est sélectionnée"
                        >
                          <mat-icon class="text-xs leading-none text-gray-9">block</mat-icon>
                          <span>Non bâti (secondaire)</span>
                        </span>
                      }
                    }

                    <button
                      type="button"
                      class="flex h-7 w-7 items-center justify-center rounded-lg text-gray-11 hover:text-white hover:bg-ruby-9 transition-colors cursor-pointer"
                      (click)="removeParcel(parcel.reference)"
                      matTooltip="Retirer cette parcelle"
                    >
                      <mat-icon class="text-base leading-none">close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        } @else {
          <div class="mt-2 flex items-center gap-2">
            <span class="text-sm font-medium text-gray-10 italic">Aucune parcelle sélectionnée</span>
          </div>
        }
      </div>
      <p class="mt-4 text-xs text-gray-11 m-0">
        Section et numéro d'ordre officiel au plan cadastral informatisé (PCI). La parcelle principale sert de
        base à l'extraction des données du bâtiment.
      </p>
    </div>

    <!-- 3. Superficie totale de la parcelle -->
    <div
      class="rounded-2xl border border-gray-6 bg-surface-default p-6 flex flex-col justify-between shadow-xs sm:col-span-3"
    >
      <div>
        <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-2">
          <span>Superficie totale</span>
          <mat-icon fontIcon="square_foot" class="text-base leading-none text-gray-11"></mat-icon>
        </div>

        <div class="mt-2 flex items-baseline gap-1.5">
          @if (totalSurface(); as surface) {
            <span class="text-3xl font-black tracking-tight text-gray-12">
              {{ surface | number: '1.0-0' : 'fr-FR' }}
            </span>
            <span class="text-base font-bold text-gray-11">m²</span>
          } @else {
            <span class="text-base font-semibold text-gray-10 italic">En attente de sélection</span>
          }
        </div>
        @if (parcels().length > 1) {
          <span class="mt-1 block text-2xs text-gray-10">Cumul de {{ parcels().length }} parcelles</span>
        }
      </div>
      <p class="mt-4 text-xs text-gray-11 m-0">Contenance foncière totale enregistrée au cadastre.</p>
    </div>
  </div>

  <!-- Interactive Satellite Map -->
  @if (hasCoordinates()) {
    <div class="rounded-2xl border border-gray-6 bg-surface-default p-6 shadow-xs space-y-4">
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div class="flex items-center gap-2">
          <mat-icon class="text-jade-11 text-lg leading-none">layers</mat-icon>
          <h3 class="text-base font-bold text-gray-12 m-0">Sélection sur le plan cadastral satellite</h3>
        </div>
        @if (!isLocked()) {
          <span
            class="text-xs text-jade-11 font-medium bg-jade-a2 border border-jade-a4 px-2.5 py-1 rounded-full"
          >
            Cliquez sur une parcelle pour l'ajouter ou la retirer
          </span>
        }
      </div>

      <etxe-property-cadastral-map
        [lat]="coordinates()!.latitude!"
        [lng]="coordinates()!.longitude!"
        [selectedReferences]="selectedReferences()"
        [highlightedParcel]="resolvedParcel()"
        [interactive]="!isLocked()"
        (parcelSelected)="onParcelSelected($event)"
      ></etxe-property-cadastral-map>
    </div>
  }

  <!-- Check obligatoire de confirmation (affiché uniquement si au moins une parcelle est sélectionnée) -->
  @if (parcels().length > 0) {
    <div class="rounded-2xl border-2 border-amber-6 bg-amber-1 p-6 shadow-sm">
      <div class="flex items-start gap-4">
        <mat-checkbox
          [checked]="isConfirmed()"
          [disabled]="isLocked()"
          (change)="onConfirmationToggle($event.checked)"
          color="primary"
          id="cadastre-confirmation-checkbox"
          class="mt-0.5"
        >
          <span class="text-sm sm:text-base font-bold text-neutral-12 leading-relaxed">
            Je confirme que
            @if (parcels().length === 1) {
              la parcelle cadastrale
              <span
                class="font-mono px-1.5 py-0.5 rounded bg-amber-3/60 border border-amber-6 text-amber-12 select-all"
              >
                {{ parcels()[0].reference }}
              </span>
              correspond bien à mon bien immobilier.
            } @else {
              les
              <span class="font-mono px-1.5 py-0.5 rounded bg-amber-3/60 border border-amber-6 text-amber-12">
                {{ parcels().length }} parcelles cadastrales
              </span>
              sélectionnées correspondent bien à mon bien immobilier, dont la parcelle principale
              @if (primaryParcel(); as primary) {
                <span
                  class="font-mono px-1.5 py-0.5 rounded bg-amber-3/60 border border-amber-6 text-amber-12 select-all"
                >
                  Section {{ primary.section }} N° {{ primary.numero }}
                </span>
              }
              portant le bâti.
            }
          </span>
        </mat-checkbox>
      </div>
      <div class="mt-3 pl-9 space-y-1.5">
        <p class="text-xs sm:text-sm text-amber-11 m-0 leading-relaxed font-medium">
          Important : la localisation géographique et les parcelles cadastrales sont définitives. Elles ne
          pourront plus être modifiées après validation de cette étape (une nouvelle annonce sera requise en
          cas d'erreur).
        </p>
        @if (primaryParcel(); as primary) {
          <p class="text-xs sm:text-sm text-amber-12/90 m-0 leading-relaxed">
            <span class="font-bold">Extraction des données de construction :</span> la parcelle principale
            <span class="font-mono font-bold">{{ primary.section }} {{ primary.numero }}</span> est
            indispensable pour interroger automatiquement le Registre national des bâtiments (BDNB). Elle
            permet de renseigner sans erreur l'année de construction, les matériaux et les niveaux de votre
            logement.
          </p>
        }
      </div>
    </div>
  }
</div>
